import { locales, localeNames } from '@aj/i18n';
import type { Locale, MessageKey } from '@aj/i18n';
import { Box, Button, Chip, HStack, Text, VStack } from '@aj/ui';
import { useEffect, useState } from 'react';

import { useFinishedGoals, useFlavour, useLifetime, useTodayView } from '../data/hooks.js';
import { rankKey, titleKey } from '../lib/labels.js';
import { resetLocal } from '../data/db.js';
import { seedDemo } from '../data/seedDemo.js';
import { requestPersistentStorage, useInstallPrompt } from '../hooks/useInstallPrompt.js';
import { BOTTOM_CLEARANCE } from '../lib/layout.js';
import { useI18n, useT } from '../providers/I18nProvider.js';
import { useTheme } from '../providers/ThemeProvider.js';
import type { ThemePreference } from '../providers/ThemeProvider.js';

/**
 * আমি — who you have become.
 *
 * The PAST PERFECT screen. Jatra is the journey in flight; this is what has
 * already settled, and the selection rule is exactly that: nothing here can
 * be reduced by a bad fortnight. Titles earned, goals finished, lifetime
 * totals — every number only ever goes up.
 *
 * That is what "calm" means structurally rather than visually. There is no
 * kept-rate, no streak, no pace and no active goal, because all four can fall,
 * and a screen where things can fall is a screen you brace before opening.
 * Duplicating them here would also make a student check two screens for the
 * same fact, which is the scoreboard habit arriving by the back door.
 *
 * Finished goals are the centrepiece. A completed Jatra is the best thing this
 * app can ever show anyone, and until now it had nowhere to live at all.
 */

/** Dev-only labels. Not translated on purpose — they never ship. */
const DEV_SEED = 'Seed 8 weeks';
const DEV_WIPE = 'Wipe';

const THEMES: { id: ThemePreference; key: MessageKey }[] = [
  { id: 'system', key: 'settings.theme.system' },
  { id: 'light', key: 'settings.theme.light' },
  { id: 'dark', key: 'settings.theme.dark' },
];

export function Profile() {
  const t = useT();
  const i18n = useI18n();
  const view = useTodayView();
  const flavour = useFlavour();
  const finished = useFinishedGoals();
  const totals = useLifetime();
  const { preference, setPreference } = useTheme();
  const { canInstall, installed, promptInstall } = useInstallPrompt();
  const [, setPersisted] = useState<boolean | null>(null);

  useEffect(() => {
    void requestPersistentStorage().then(setPersisted);
  }, []);

  return (
    <VStack
      as="main"
      gap={8}
      className={`min-h-0 flex-1 overflow-y-auto px-5 pt-8 ${BOTTOM_CLEARANCE}`}
    >
      {/* Centred and unhurried. Nothing on this screen is an action. */}
      <VStack gap={2} align="center">
        <Text variant="title" tone="ink">
          {view.name ?? t('app.name')}
        </Text>
        <Chip tone="accent">{t(rankKey(flavour, view.rank))}</Chip>
      </VStack>

      {view.titles.length > 0 && (
        <HStack gap={2} justify="center" className="flex-wrap">
          {view.titles.map((title) => (
            <Chip key={title} tone="neutral">
              {t(titleKey(flavour, title))}
            </Chip>
          ))}
        </HStack>
      )}

      {finished.length > 0 && (
        <VStack gap={3}>
          <Text variant="overline" tone="subtle">
            {t('me.finished')}
          </Text>
          <VStack as="ul" gap={2} aria-label={t('me.finished')}>
            {finished.map((goal) => (
              <HStack as="li" key={goal.id} gap={3} align="baseline" justify="between">
                <Text variant="body" tone="ink" className="min-w-0 truncate">
                  {goal.title}
                </Text>
                <Text variant="caption" tone="subtle" numeric className="shrink-0">
                  {i18n.formatDate(Date.parse(goal.on), { month: 'short', year: 'numeric' })}
                </Text>
              </HStack>
            ))}
          </VStack>
        </VStack>
      )}

      {/* One line, four facts, no chart. This screen is a record, not a
          dashboard — the moment it grows a graph it becomes something to
          check rather than something to look back on. */}
      {totals.daysRecorded > 0 && (
        <Text variant="caption" tone="muted" numeric className="text-center text-pretty">
          {t('me.lifetime', {
            days: totals.daysRecorded,
            duration: i18n.formatDuration(totals.minutes),
            kept: totals.promisesKept,
          })}
        </Text>
      )}

      {/* Development only, and stripped from the bundle in production by the
          `import.meta.env.DEV` constant folding. Most of what this app draws
          needs weeks of history to exist at all, so without this there is no
          way to look at a layout without waiting two months. */}
      {import.meta.env.DEV && (
        <HStack gap={2} wrap justify="center">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void seedDemo().then(() => window.location.reload())}
          >
            {DEV_SEED}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void resetLocal().then(() => window.location.reload())}
          >
            {DEV_WIPE}
          </Button>
        </HStack>
      )}

      <Box className="border-t border-line pt-6">
        <VStack gap={5}>
          {canInstall && !installed && (
            <Button icon="download" fullWidth onClick={() => void promptInstall()}>
              {t('action.install')}
            </Button>
          )}

          <VStack gap={2}>
            <Text variant="overline" tone="subtle">
              {t('settings.theme')}
            </Text>
            <HStack gap={2} wrap>
              {THEMES.map((option) => (
                <Button
                  key={option.id}
                  size="sm"
                  variant={preference === option.id ? 'primary' : 'secondary'}
                  onClick={() => setPreference(option.id)}
                >
                  {t(option.key)}
                </Button>
              ))}
            </HStack>
          </VStack>

          {locales.length > 1 && (
            <VStack gap={2}>
              <Text variant="overline" tone="subtle">
                {t('settings.language')}
              </Text>
              <HStack gap={2}>
                {locales.map((locale: Locale) => (
                  <Button
                    key={locale}
                    size="sm"
                    variant={i18n.locale === locale ? 'primary' : 'secondary'}
                    onClick={() => i18n.setLocale(locale)}
                  >
                    {localeNames[locale]}
                  </Button>
                ))}
              </HStack>
            </VStack>
          )}

          <Text variant="caption" tone="subtle">
            {t('app.tagline')}
          </Text>
        </VStack>
      </Box>
    </VStack>
  );
}
